import type {
  EstimationItemResponse,
  EstimationModelItemParameterResponse,
  EstimationModelResponse,
  MethodologyResponse,
} from '@/api/schema/data-contracts';
import { sortExpressions } from '@/utils/sorting';

import type { ItemWithParentId } from './model';

// recursively arrange items so that descendants follow its immediate parent
export const reorderChildrenAfterItsParentRecursively = <T extends ItemWithParentId>(
  sortedItems: T[],
  parentId: string | undefined,
): T[] => {
  const result: T[] = [];

  // Find all items with the given parentId
  sortedItems
    .filter((item) => (item.parentId ?? undefined) === parentId)
    .forEach((item) => {
      // Add the parent item to the result list
      result.push(item);

      // Recursively add all of its children (and grandchildren, etc.)
      const children = reorderChildrenAfterItsParentRecursively(sortedItems, item.id);
      result.push(...children);
    });

  return result;
};

export const getDescendants = <T extends ItemWithParentId>(
  items: T[],
  parentId: string | undefined,
): T[] => {
  const descendants: T[] = [];

  // Find all items that have the current parentId
  const children = items.filter((item) => (item.parentId ?? undefined) === parentId);

  // For each child, add it to the descendants list and recursively find its children
  children.forEach((child) => {
    descendants.push(child);
    descendants.push(...getDescendants(items, child.id)); // Recursively add all descendants
  });

  return descendants;
};

export function getDistinctParameterNamesSorted(
  estimationItems: EstimationItemResponse[],
  methodologies: MethodologyResponse[],
  estimationModel: EstimationModelResponse,
) {
  const paramNames: string[] = [];

  const relevantMethodologyIds = estimationItems
    .filter((x) => x.methodologyId)
    .map((x) => x.methodologyId!);

  // methodology params first
  methodologies
    .filter((methodology) => relevantMethodologyIds.includes(methodology.id))
    .sort(sortExpressions.byOrder)
    .forEach((methodology) => {
      methodology.defaultParameters
        .filter((methodologyParameter) => !paramNames.includes(methodologyParameter.label))
        .sort(sortExpressions.byOrder)
        .forEach((x) => paramNames.push(x.label));
    });

  const estimationModelItemParameterById = new Map<string, EstimationModelItemParameterResponse>(
    estimationModel.items.flatMap((x) => x.parameters).map((x) => [x.id, x]),
  );

  // estimation model item params
  estimationItems.forEach((estimationItem) => {
    estimationItem.parameters
      .filter((x) => !!x.estimationModelItemParameterId)
      .sort(sortExpressions.byOrder)
      .forEach((x) => {
        const modelParameter = estimationModelItemParameterById.get(
          x.estimationModelItemParameterId!,
        )!;
        if (!paramNames.includes(modelParameter.label)) {
          paramNames.push(modelParameter.label);
        }
      });
  });

  return paramNames;
}
